import { useState, useCallback, useMemo } from 'react';
import { startOfDay } from 'date-fns';
import s from './history.module.scss'
import { IconUsers } from '@tabler/icons-react';
import { PageTemplate } from '@shared/ui/page-template';
import { Button } from '@shared/ui/button/button';
import { SupportButton } from '@shared/ui/support-button';
import { Dropdown } from '@shared/ui/dropdown/dropdown';
import { HistoryBanner } from '@shared/ui/history-banner/history-banner';
import { usePackagesModal } from '@features/teacher/packages';
import { useProfilePackages } from '@features/teacher/packages/api/useProfilePackages';
import { MAX_PACKAGES } from '@features/teacher/packages/config';
import { useStudentModal } from '@features/teacher/student/model/use-student-modal';
import { useRemoveAppointmentModal } from '@features/appointment/remove-appointment/model/use-remove-appointment-modal';
import { HistoryModals } from '@widgets/history/ui/history-modals';
import { useAppointmentsHistory } from '@widgets/history/api/useAppointmentsHistory';
import { useAllAppointments } from '@entities/schedule/api/useAllAppointments';
import { useActivePacks } from '@widgets/history/api/useActivePacks';
import { useArchivedPacks } from '@widgets/history/api/useArchivedPacks';
import { useAttachReceipt } from '@widgets/history/api/useAttachReceipt';
import { LessonsList } from '@widgets/history/ui/lessons-list';
import { PacksList } from '@widgets/history/ui/packs-list';
import { useHistoryData, FILTER_OPTIONS } from '@widgets/history/lib/useHistoryData';
import { mapTeacherPack, mapTeacherLesson } from '@widgets/history/lib/mappers';
import { toast } from 'react-toastify';

const TODAY_START = startOfDay(new Date()).toISOString()

export const History = () => {
    const [switchActive, setSwitchActive] = useState(false)
    const [filter, setFilter] = useState(null)

    const { data: activePacks } = useActivePacks()
    const { data: archivePacks } = useArchivedPacks()
    const { data: profilePackages } = useProfilePackages()
    const { data: history, fetchNextPage, hasNextPage, isFetchingNextPage } = useAppointmentsHistory()
    const { data: upcoming } = useAllAppointments({ from: TODAY_START })
    const { mutateAsync: attachReceipt } = useAttachReceipt()

    const profilePackagesList = useMemo(() =>
        Array.isArray(profilePackages)
            ? profilePackages
            : profilePackages?.data ?? [],
        [profilePackages]
    )
    const packagesCreated = profilePackagesList.length

    const { open: openPackagesModal } = usePackagesModal()
    const { open: openStudentModal } = useStudentModal()
    const { open: openRemoveAppointmentModal } = useRemoveAppointmentModal()

    const handleOpenCancelModal = useCallback((appointmentId) => {
        openRemoveAppointmentModal({ appointmentId })
    }, [openRemoveAppointmentModal])

    const handleAttachReceipt = useCallback(async (id, file) => {
        try {
            await attachReceipt({ id, data: file })
            toast.success('Чек загружен')
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Ошибка сервера. Попробуйте позже')
        }
    }, [attachReceipt])

    const { packsToShow, lessonsToShow, activeCount, completedCount } = useHistoryData({
        switchActive,
        filter,
        activePacks,
        archivePacks,
        history,
        upcoming,
    })

    return (
        <PageTemplate>
            <HistoryModals />
            <HistoryBanner
                role='teacher'
                onCreate={() => openPackagesModal({ mode: 'create' })}
                onEdit={() => openPackagesModal({ mode: 'edit' })}
                packagesCreated={packagesCreated}
                packagesAvailable={MAX_PACKAGES}
            />
            <div className={s.container}>
                <div className={s.header}>
                    <h5 className={s.headText}>Мои занятия</h5>
                    <div className={s.countLessons}>
                        {activeCount} активных • {completedCount} завершённых
                    </div>
                </div>
                <div className={s.buttonGroup}>
                    <div className={s.leftGroup}>
                        <Button
                            label='Пакеты'
                            className={switchActive ? s.switchButton : s.activeButton}
                            onClick={() => setSwitchActive(false)}
                        />
                        <Button
                            label='Одиночные'
                            className={switchActive ? s.activeButton : s.switchButton}
                            onClick={() => setSwitchActive(true)}
                        />
                    </div>
                    <Dropdown
                        buttonClassName={s.filter}
                        label='Все'
                        options={FILTER_OPTIONS}
                        value={filter}
                        onChange={setFilter}
                    />
                </div>
                {switchActive ? (
                    <LessonsList
                        key={`lessons-${filter ?? 'all'}`}
                        lessons={lessonsToShow}
                        role='teacher'
                        mapLesson={mapTeacherLesson}
                        onOpenCancelModal={handleOpenCancelModal}
                        onAttachReceipt={handleAttachReceipt}
                    />
                ) : (
                    <PacksList
                        key={`packs-${filter ?? 'all'}`}
                        packs={packsToShow}
                        role='teacher'
                        mapPack={mapTeacherPack}
                        onOpenStudent={openStudentModal}
                    />
                )}
                {switchActive && hasNextPage && (
                    <div className={s.showMore}>
                        <Button
                            theme='secondary'
                            label={isFetchingNextPage ? 'Загрузка...' : 'Показать ещё'}
                            disabled={isFetchingNextPage}
                            onClick={() => fetchNextPage()}
                        />
                    </div>
                )}
                <div className={s.footer}>
                    <div className={s.leftBlock}>
                        <div className={s.circle}>
                            <IconUsers className={s.icon} size={24} />
                        </div>
                        <div className={s.text}>
                            Нужна помощь? <br />
                            <span className={s.help}>Мы всегда на связи и готовы помочь.</span>
                        </div>
                    </div>
                    <SupportButton
                        className={s.supportWrapper}
                        buttonClassName={s.buttonHelp}
                        label='Написать в поддержку'
                    />
                </div>
            </div>
        </PageTemplate>
    )
}
